import {useState} from 'react';

import {useTranslation} from 'react-i18next';

import EditIcon from '@mui/icons-material/Edit';
import {Grid, IconButton, Typography} from '@mui/material';

import {CompanyProfile} from '../types';

import {CompanyProfileLocationsTable} from './CompanyProfileLocationsTable';
import {CompanyProfileLocationsForm} from './forms/CompanyProfileLocationsForm';
import {PROFILE_DIALOG_SX, PROFILE_DIALOG_TITLE_SX} from './profileDialogStyles';
import {ProfileSection} from './ProfileSection';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';

type CompanyProfileLocationsProps = {
  company: CompanyProfile;
  upsertLocations(data: CompanyProfile['locations']): void;
  deleteLocations(locationId: string[]): void;
};

export const CompanyProfileLocations = ({
  company,
  upsertLocations,
  deleteLocations,
}: CompanyProfileLocationsProps) => {
  const {t} = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFormSubmit = async (data: CompanyProfile['locations'], deletedIds: Set<string>) => {
    if (deletedIds.size > 0) {
      await deleteLocations([...deletedIds]);
    }
    await upsertLocations(data.filter((location) => !deletedIds.has(location.resourceId)));
    toggleModal();
  };

  const toggleModal = () => {
    setIsModalOpen((prevModalOpen) => !prevModalOpen);
  };

  const handleFormCancel = () => {
    toggleModal();
  };

  return (
    <ProfileSection
      title={t('companyProfile.locations.title')}
      dataTour='profile.locations'
      actions={
        <IconButton onClick={toggleModal}>
          <EditIcon />
        </IconButton>
      }
    >
      <Grid container item xs={12}>
        {company.locations.length !== 0 ? (
          <CompanyProfileLocationsTable locations={company.locations} />
        ) : (
          <Grid container direction='column' alignItems='center'>
            <Typography pb={2} textAlign={'center'} sx={{opacity: 0.3}}>
              {t('companyProfile.locations.emptyState')}
            </Typography>
          </Grid>
        )}
      </Grid>
      <EmpionDialog
        open={isModalOpen}
        onClose={toggleModal}
        maxWidth='md'
        showCloseButton
        title={t('companyProfile.locations.dialog.title')}
        sxTitle={PROFILE_DIALOG_TITLE_SX}
        sx={PROFILE_DIALOG_SX}
      >
        <CompanyProfileLocationsForm
          locations={company.locations}
          onCancel={handleFormCancel}
          onSubmit={handleFormSubmit}
        />
      </EmpionDialog>
    </ProfileSection>
  );
};
